import { makeStyles, TextField, Theme } from '@material-ui/core';
import React, { ChangeEvent, SyntheticEvent, useState } from 'react';

import {
  ChannelInput,
  PlexInputError,
  PlexMember,
  PlexMemberRole,
  PlexRegisteredUser,
  SearchUser,
  useGetLoggedInUserQuery,
} from '../../../../graphql/gen/types';
import { colorPalette, spacings, TextSizesEnum } from '../../../../theme';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { ColorSelector } from './form_components/ColorSelector';
import { DeletePlex } from './form_components/DeletePlex';
import { GoogleGroups } from './form_components/GoogleGroups';
import { MembersList } from './form_components/MembersList';
import { NewCollaboratorSearch } from './form_components/NewCollaboratorSearch';
import { PlexChannelSection } from './form_components/PlexChannelSection';
import { PlexGoogleCalendarsSection } from './form_components/PlexGoogleCalendarsSection';
import { PlexFormDataUpdate, PlexFormGoogleCalendar } from './form_components/types';
import { plexMemberUser, plexUserRoleTargetKey } from './plexUserRoleTargetKey';

export type PlexFormData = {
  name: string;
  color: string | undefined | null;
  description: string;
  isCurrentUserOwner: boolean;
  userRoles: Array<PlexMember>;
  channels: ChannelInput[];
  googleGroups: {
    leads: string | null;
    team: string | null;
    stakeholders: string | null;
  } | null;
  googleCalendars: PlexFormGoogleCalendar[];
};

type Props = {
  mode: 'create' | 'update';
  plex: PlexFormData;
  saveError: PlexInputError | null;
  onPlexChange: PlexFormDataUpdate;
  onPlexDelete: () => void;
};

type State = {
  flashUserRowKey: string | null;
};

const NAME_MAX_LENGTH = 80;
const DESCRIPTION_MAX_LENGTH = 280;

export const PlexDialogContent = React.memo((props: Props): React.ReactElement | null => {
  const { plex, mode, saveError, onPlexChange, onPlexDelete } = props;
  const [state, setState] = useState<State>({
    flashUserRowKey: null,
  });
  const { data } = useGetLoggedInUserQuery({ variables: {} });
  const classes = useStyles();

  const handleNewCollaboratorFlashFinished = React.useCallback((): void => {
    setState({ flashUserRowKey: null });
  }, [setState]);

  const handleCollaboratorDeletion = React.useCallback(
    (index: number): void => {
      onPlexChange((prevPlex) => ({
        ...prevPlex,
        userRoles: prevPlex.userRoles.filter((_, idx) => idx !== index),
      }));
    },
    [onPlexChange]
  );

  const handleRoleChange = React.useCallback(
    (index: number, role: PlexMemberRole): void => {
      onPlexChange((prevPlex) => ({
        ...prevPlex,
        userRoles: prevPlex.userRoles.map((userRole, idx) => {
          if (idx === index) {
            return { ...userRole, role };
          } else {
            return userRole;
          }
        }),
      }));
    },
    [onPlexChange]
  );

  // Disable current registered user and any owners from being removed if user is not owner
  const disabledUserKeys = React.useMemo(() => {
    if (data?.currentUser == null) {
      return new Set<string>();
    }
    const { id, email, name, picture } = data?.currentUser;

    const currentRegisteredUser: PlexRegisteredUser = {
      __typename: 'PlexRegisteredUser',
      id,
      email,
      name,
      picture,
    };

    const disabledUserKeysSet = new Set([plexUserRoleTargetKey(currentRegisteredUser)]);
    if (mode === 'update' && !plex.isCurrentUserOwner)
      plex.userRoles.forEach((userRole) => {
        if (userRole.role === PlexMemberRole.Owner) {
          disabledUserKeysSet.add(plexUserRoleTargetKey(userRole.user));
        }
      });
    return disabledUserKeysSet;
  }, [data, mode, plex.isCurrentUserOwner, plex.userRoles]);

  const handleAddChannel = React.useCallback(
    (channel: ChannelInput): void => {
      onPlexChange((prevPlex) => ({
        ...prevPlex,
        channels: [...prevPlex.channels, channel],
      }));
    },
    [onPlexChange]
  );

  const handleRemoveChannel = React.useCallback(
    (channel: ChannelInput): void => {
      onPlexChange((prevPlex) => ({
        ...prevPlex,
        channels: prevPlex.channels.filter((c) => c.externalId !== channel.externalId),
      }));
    },
    [onPlexChange]
  );

  const handleAddGoogleCalendar = React.useCallback(
    (calendar: PlexFormGoogleCalendar): void => {
      onPlexChange((prevPlex) => ({
        ...prevPlex,
        googleCalendars: [...prevPlex.googleCalendars, calendar],
      }));
    },
    [onPlexChange]
  );

  const handleRemoveGoogleCalendar = React.useCallback(
    (calendar: PlexFormGoogleCalendar): void => {
      onPlexChange((prevPlex) => ({
        ...prevPlex,
        googleCalendars: prevPlex.googleCalendars.filter((c) => c.googleCalendarId !== calendar.googleCalendarId),
      }));
    },
    [onPlexChange]
  );

  const handleAddCollaborator = React.useCallback(
    (collaborator: SearchUser): void => {
      const newUser = plexMemberUser(collaborator);
      const newCollaboratorKey = plexUserRoleTargetKey(newUser);
      // if user already exists, do not update anything
      for (const userRole of plex.userRoles) {
        if (!!userRole.user.email && userRole.user.email === collaborator.email) {
          setState({ flashUserRowKey: plexUserRoleTargetKey(userRole.user) });
          return;
        }
        if (newCollaboratorKey === plexUserRoleTargetKey(userRole.user)) {
          return;
        }
      }

      onPlexChange((prevPlex) => ({
        ...prevPlex,
        userRoles: [...plex.userRoles, { user: newUser, role: PlexMemberRole.Writer }],
      }));
      setState({ flashUserRowKey: newCollaboratorKey });
    },
    [onPlexChange, plex.userRoles]
  );

  if (data?.currentUser == null) {
    return null;
  }

  const handleNameChange = (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const name = event.currentTarget.value;
    onPlexChange((prevPlex) => ({
      ...prevPlex,
      name,
    }));
  };

  const handleColorChange = (event: ChangeEvent<{ name?: string | undefined; value: unknown }>): void => {
    const color = event.target.value as string;
    onPlexChange((prevPlex) => ({
      ...prevPlex,
      color,
    }));
  };

  const handleDescriptionChange = (event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const description = event.currentTarget.value;
    onPlexChange((prevPlex) => ({
      ...prevPlex,
      description,
    }));
  };

  // TODO: form input validation
  // FIXME: Dialog usage causes a console warning, more details in https://github.com/mui-org/material-ui/issues/13394
  return (
    <div className={classes.root}>
      <div className={classes.headerContainer}>
        <PlexTypography className={classes.headerTitle} text={'General'} size={TextSizesEnum.Large} />
      </div>

      <div className={classes.nameContainer}>
        <TextField
          autoFocus
          required
          className={classes.plexNameInput}
          autoComplete={'off'}
          margin={'dense'}
          error={plex.name.length > NAME_MAX_LENGTH}
          label={'Name'}
          type={'text'}
          value={plex.name}
          onChange={handleNameChange}
          aria-required={true}
          helperText={`${plex.name.length} / ${NAME_MAX_LENGTH}`}
        />
        <ColorSelector plexColor={plex.color} handleColorChange={handleColorChange} />
      </div>
      <TextField
        multiline
        className={classes.plexDescriptionInput}
        autoComplete={'off'}
        margin={'dense'}
        type={'text'}
        label={'Description'}
        value={plex.description}
        onChange={handleDescriptionChange}
        helperText={`${plex.description.length} / ${DESCRIPTION_MAX_LENGTH}`}
      />

      <div className={classes.headerContainer}>
        <PlexTypography className={classes.headerTitle} text={'Collaborators'} size={TextSizesEnum.Large} />
        <NewCollaboratorSearch onAdd={handleAddCollaborator} existingMembers={plex.userRoles} />
      </div>
      {/* TODO(https://github.com/all-hands-hq/source/issues/107): Revize based on currentUser permissions to this plex */}
      <MembersList
        disabledKeys={disabledUserKeys}
        flashKey={state.flashUserRowKey}
        userRoles={plex.userRoles}
        saveErorrs={saveError?.addedMembers || []}
        onFlashFinished={handleNewCollaboratorFlashFinished}
        onChangeRole={handleRoleChange}
        onDeleteRow={handleCollaboratorDeletion}
      />

      <div className={classes.headerContainer}>
        <PlexTypography className={classes.headerTitle} text={'Channel'} size={TextSizesEnum.Large} />
      </div>
      <PlexChannelSection
        channels={plex.channels}
        onAddChannel={handleAddChannel}
        onRemoveChannel={handleRemoveChannel}
      />

      <div className={classes.headerContainer}>
        <PlexTypography className={classes.headerTitle} text={'Google Calendars'} size={TextSizesEnum.Large} />
      </div>
      <PlexGoogleCalendarsSection
        googleCalendars={plex.googleCalendars}
        onAddGoogleCalendar={handleAddGoogleCalendar}
        onRemoveGoogleCalendar={handleRemoveGoogleCalendar}
      />

      <DeletePlex mode={mode} onPlexDelete={onPlexDelete} />

      {false && <GoogleGroups plexName={plex.name} plexGoogleGroups={plex.googleGroups} onPlexChange={onPlexChange} />}
    </div>
  );
});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    flex: 1,
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    height: 68,
    backgroundColor: colorPalette.purpleGray._50,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: spacings.xlarge,
    paddingRight: spacings.xlarge,
  },
  headerTitle: {
    color: colorPalette.dark._800,
    fontWeight: 600,
  },
  nameContainer: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: spacings.xlarge,
    paddingRight: spacings.xlarge,
  },
  plexNameInput: {
    display: 'flex',
    marginTop: spacings.small,
    flex: 1,
    marginRight: spacings.xlarge,
  },
  plexDescriptionInput: {
    display: 'flex',
    flexShrink: 0,
    marginTop: spacings.xsmall,
    marginLeft: spacings.xlarge,
    marginRight: spacings.xlarge,
  },
  colorSelect: {
    height: 25,
    width: 200,
    display: 'flex',
    flexBasis: 200,
    marginTop: 3,
    marginBottom: 17,
  },
}));
